import React from 'react'
import ReactDOM from 'react-dom'
import { Close } from '@carbon/icons-react'
import { cn } from '../../utils/cn'

export const CloseButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { size?: 'sm' | 'md' | 'lg' }
>(({ className, size = 'md', ...props }, ref) => {
  const sizeMap = { sm: 14, md: 18, lg: 22 }
  return (
    <button
      ref={ref}
      type="button"
      aria-label="Close"
      className={cn(
        'inline-flex items-center justify-center rounded-md p-1.5 text-content-muted transition-colors hover:bg-surface-subtle hover:text-content-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
        className,
      )}
      {...props}
    >
      <Close size={sizeMap[size]} />
    </button>
  )
})
CloseButton.displayName = 'CloseButton'

export const Code = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  ({ className, ...props }, ref) => (
    <code
      ref={ref}
      className={cn(
        'rounded bg-surface-subtle px-1.5 py-0.5 font-mono text-[0.85em] text-content-primary',
        className,
      )}
      {...props}
    />
  ),
)
Code.displayName = 'Code'

export const Kbd = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  ({ className, ...props }, ref) => (
    <kbd
      ref={ref}
      className={cn(
        'rounded border border-line-subtle bg-surface-subtle px-1.5 py-0.5 font-mono text-xs text-content-secondary',
        className,
      )}
      {...props}
    />
  ),
)
Kbd.displayName = 'Kbd'

export const List = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn('flex flex-col gap-1', className)} {...props} />
  ),
)
List.displayName = 'List'

export const UnorderedList = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(({ className, ...props }, ref) => (
  <ul ref={ref} className={cn('list-disc space-y-1 pl-5', className)} {...props} />
))
UnorderedList.displayName = 'UnorderedList'

export const OrderedList = React.forwardRef<
  HTMLOListElement,
  React.HTMLAttributes<HTMLOListElement>
>(({ className, ...props }, ref) => (
  <ol ref={ref} className={cn('list-decimal space-y-1 pl-5', className)} {...props} />
))
OrderedList.displayName = 'OrderedList'

export const ListItem = React.forwardRef<HTMLLIElement, React.HTMLAttributes<HTMLLIElement>>(
  ({ className, ...props }, ref) => (
    <li ref={ref} className={cn('text-content-secondary', className)} {...props} />
  ),
)
ListItem.displayName = 'ListItem'

export const ListIcon = ({ as: As, className }: { as: React.ElementType; className?: string }) => (
  <As className={cn('mr-2 inline', className)} />
)

export const Circle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { size?: string }
>(({ className, size, style, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center justify-center rounded-full', className)}
    style={{ width: size, height: size, ...style }}
    {...props}
  />
))
Circle.displayName = 'Circle'

export const Square = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { size?: string }
>(({ className, size, style, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center justify-center', className)}
    style={{ width: size, height: size, ...style }}
    {...props}
  />
))
Square.displayName = 'Square'

export const Image = React.forwardRef<
  HTMLImageElement,
  React.ImgHTMLAttributes<HTMLImageElement> & { fallbackSrc?: string }
>(({ className, fallbackSrc, onError, ...props }, ref) => (
   
  <img
    ref={ref}
    className={className}
    onError={e => {
      if (fallbackSrc && e.currentTarget.src !== fallbackSrc) e.currentTarget.src = fallbackSrc
      onError?.(e)
    }}
    {...props}
  />
))
Image.displayName = 'Image'

export const VisuallyHidden = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => (
  <span ref={ref} className={cn('sr-only', className)} {...props} />
))
VisuallyHidden.displayName = 'VisuallyHidden'

export const AbsoluteCenter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2', className)}
    {...props}
  />
))
AbsoluteCenter.displayName = 'AbsoluteCenter'

export interface CollapseProps extends React.HTMLAttributes<HTMLDivElement> {
  in?: boolean
  animateOpacity?: boolean
  unmountOnExit?: boolean
}

export const Collapse = React.forwardRef<HTMLDivElement, CollapseProps>(
  ({ in: isOpen, animateOpacity = true, unmountOnExit, className, children, ...props }, ref) => {
    if (unmountOnExit && !isOpen) return null
    return (
      <div
        ref={ref}
        className={cn(
          'grid transition-all duration-200 ease-out',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          animateOpacity && (isOpen ? 'opacity-100' : 'opacity-0'),
          className,
        )}
        {...props}
      >
        <div className="overflow-hidden">{children}</div>
      </div>
    )
  },
)
Collapse.displayName = 'Collapse'

export function Portal({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = React.useState(false)
  React.useEffect(() => setMounted(true), [])
  if (!mounted || typeof document === 'undefined') return null
  return ReactDOM.createPortal(children, document.body)
}
